import { Button, Divider, Flex, Input, LoadingOverlay, Modal } from "@mantine/core";
import { IconEdit, IconSearch, IconTrash } from "@tabler/icons-react";
import { useRef, useState } from "react";
import { useNavigate } from "react-router";
import { categoryApi } from "../../apis";
import { ICategory } from "../../interfaces/ICategory";
import { Dialog } from "../../utils/confirm-modal";
import HttpHandler from "../../utils/http-handler";
import { Message } from "../../utils/message";
import { PageLayout } from "../layout";
import { ColumnType, ITableRef, Table } from "../table";
import { Tag } from "../tags";
import RenderText from "../ui/render-text";
import { SubCategoryFormEdit } from "./sub-categories-form";
import { SubCategoryForm } from "./sub-category-form";

export function SubCategoryList() {
  const navigate = useNavigate();
  const [action, setAction] = useState<string[]>([]);
  const ref = useRef<ITableRef>(null);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState<string>("");

  const columns = useHeader({
    onClick: (key, item) => {
      switch (key) {
        case "edit": {
          setAction(["update", item as any]);
          break;
        }

        case "remove": {
          Dialog.confirm("Та үүнийг устгахдаа итгэлтэй байна уу?", async (key) => {
            switch (key) {
              case "confirm": {
                setLoading(true);
                try {
                  let res = await categoryApi.remove(item.id);
                  if (res.success) {
                    setTimeout(() => {
                      ref.current?.reload();
                    }, 200);
                  }
                  Message.success("Амжилттай категорыг устгалаа!");
                  setLoading(false);
                } catch (err) {
                  setLoading(false);
                  Message.error((err as HttpHandler)?.message!);
                }
                break;
              }

              default:
            }
          });
          break;
        }
      }
    },
  });

  const onCancel = async () => {
    setAction([]);
    setAction([]);
  };

  const reload = () => {
    setTimeout(() => {
      ref.current?.reload();
    }, 200);
  };

  if (loading) return <LoadingOverlay visible={loading} />;

  return (
    <>
      <PageLayout
        title="Дэд категори"
        subTitle="Бараа бүтээгдэхүүн, ажил үйлчилгээний дэд категорийн жагсаалт"
        extra={[
          <Input leftSection={<IconSearch />} placeholder="Нэрээр хайх" onChange={(e) => setQuery(e.currentTarget.value)} />,
          <Button variant="default" onClick={() => navigate("/reference/product-list")}>
            Буцах
          </Button>,
          <Button
            onClick={() => {
              setAction(["new"]);
            }}>
            Нэмэх
          </Button>,
        ]}>
        <Divider my={10} />
        <Table
          ref={ref}
          name="sub-category.list"
          columns={columns}
          filters={{ query: query, type: "SUB_CATEGORY" }}
          loadData={(data) => categoryApi.list(data!)}
        />
      </PageLayout>
      <Modal opened={action[0] === "new"} onClose={() => setAction([])} withCloseButton={false} centered size="60%">
        <SubCategoryForm title="Дэд категорийн мэдээлэл" subTitle="Дэд категорийн нэр, тайлбар, талбаруудыг нэмэх" onCancel={onCancel} reload={reload} />
      </Modal>
      <Modal opened={action && action[0] === "update"} onClose={() => setAction([])} withCloseButton={false} centered size={"60%"}>
        <SubCategoryFormEdit
          title="Дэд категорийн мэдээлэл"
          subTitle="Дэд категорийн нэр, тайлбар, засах"
          onCancel={onCancel}
          action={action}
          reload={reload}
        />
      </Modal>
    </>
  );
}

const useHeader = ({ onClick }: { onClick: (key: string, record: ICategory) => void }): ColumnType<ICategory>[] => [
  {
    title: "#",
    width: "30px",
    render: (_r, index) => <RenderText text={index + 1} />,
  },
  {
    title: "Үйлдэл",
    render: (record) => {
      return (
        <Flex gap="sm">
          <Button variant="light" radius={100} w={35} h={35} p={0} onClick={() => onClick("edit", record)}>
            <IconEdit />
          </Button>
          <Button variant="light" color="red" radius={100} w={35} h={35} p={0} onClick={() => onClick("remove", record)}>
            <IconTrash />
          </Button>
        </Flex>
      );
    },
  },
  {
    title: "Нэр",
    dataIndex: "name",
    render: (record) => <RenderText text={record?.name || "-"} />,
  },
  {
    title: "Код",
    dataIndex: "refCode",
    render: (record) => <RenderText text={record?.refCode ?? "-"} />,
  },
  {
    title: "Нэр төрөл",
    dataIndex: "itemType",
    render: (record) => <RenderText text={record?.itemType?.name ?? "-"} />,
  },
  {
    title: "Ангилал",
    dataIndex: "classification",
    render: (record) => <RenderText text={record?.classification?.name ?? "-"} />,
  },
  {
    title: "Дэд ангилал",
    dataIndex: "subClassification",
    render: (record) => <RenderText text={record?.subClassification?.name || "-"} />,
  },
  {
    title: "Категори",
    dataIndex: "category",
    render: (record) => <RenderText text={record?.category?.name || "-"} />,
  },
  {
    title: "Тайлбар",
    dataIndex: "description",
    render: (record) => <RenderText text={record?.description || "-"} />,
  },
  {
    title: "Нээлттэй эсэх",
    dataIndex: "isPublic",
    render: (record) => <Tag name={record.isPublic ? "Тийм" : "Үгүй"} color={record.isPublic ? "green.5" : "red.5"} />,
  },
];
